/*  src/pages/LogInPage.tsx  */
import React, { useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  TextField,
} from '@mui/material';
import AuthCard from '../components/AuthCard.tsx';
import AreaChoiceCard from '../components/AreaChoiceCard.tsx';
import GoogleAuthButton from '../components/GoogleAuthButton.tsx';
import { supabase } from '../supabaseClient.ts';
import { getRememberSession, setRememberSession } from '../utils/authStorage.ts';
import {
  applySignedInRoute,
  routeAfterSignIn,
  type AreaChoice,
} from '../utils/routeAfterSignIn.ts';

const LogInPage: React.FC = () => {
  const location = useLocation();
  const handoffError = (location.state as { authError?: string } | null)?.authError ?? null;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const [error, setError] = useState<string | null>(handoffError);
  const [info, setInfo] = useState<string | null>(null);

  const [loading, setLoading] = useState(false);
  const [choice, setChoice] = useState<AreaChoice | null>(null);
  const [rememberMe, setRememberMe] = useState(() => getRememberSession());

  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);

    if (!email || !password) {
      setError('Please enter both email and password.');
      return;
    }

    setLoading(true);
    setRememberSession(rememberMe);

    const { data, error: signInErr } =
      await supabase.auth.signInWithPassword({ email, password });

    if (signInErr || !data.user || !data.session) {
      setError(signInErr?.message || 'Login failed.');
      setLoading(false);
      return;
    }

    const result = await routeAfterSignIn(data.user);
    applySignedInRoute(result, data.session, { navigate, setChoice, setError });
    setLoading(false);
  };

  const handleReset = async () => {
    setError(null);
    setInfo(null);

    if (!email) {
      setError('Enter your email first.');
      return;
    }

    setLoading(true);
    const { error: resetErr } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setLoading(false);

    if (resetErr) setError(resetErr.message);
    else setInfo('Check your email for a reset link.');
  };

  if (choice) {
    return <AreaChoiceCard choice={choice} navigate={navigate} />;
  }

  return (
    <AuthCard title="Log in" onSubmit={handleSubmit}>
      <GoogleAuthButton disabled={loading} remember={rememberMe} onError={setError} />

      <Divider>or</Divider>

      <TextField
        label="Email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@example.com"
        autoComplete="email"
        fullWidth
        required
        autoFocus
      />

      <TextField
        label="Password"
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        autoComplete="current-password"
        fullWidth
        required
      />

      <FormControlLabel
        control={
          <Checkbox
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            size="small"
          />
        }
        label="Remember me"
      />

      {error && <Alert severity="error">{error}</Alert>}
      {info && <Alert severity="success">{info}</Alert>}

      <Button
        type="submit"
        variant="contained"
        size="large"
        disabled={loading}
        startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
      >
        {loading ? 'Signing in…' : 'Log in'}
      </Button>

      <Button type="button" onClick={handleReset} disabled={loading}>
        Forgot password?
      </Button>

      <Button component={RouterLink} to="/signup" variant="outlined">
        Create an account
      </Button>
    </AuthCard>
  );
};

export default LogInPage;
